
import { Category } from '@/types/assessment';
import { PersonalInfo, AssessmentStep } from './types/progressTypes';

// Calculate total questions across all categories
export function calculateTotalQuestions(categories: Category[]): number {
  return categories.reduce((sum, category) => sum + category.questions.length, 0);
}

// Calculate completed questions count up to a certain category and question
export function calculateCompletedQuestionsCount(
  categories: Category[],
  currentCategoryIndex: number,
  currentQuestionIndex: number
): number {
  return categories.slice(0, currentCategoryIndex).reduce(
    (sum, category) => sum + category.questions.length, 0
  ) + (currentQuestionIndex + 1);
}

// Calculate navigation progress percentage
export function calculateNavigationProgressPercentage(
  totalQuestions: number,
  completedQuestionsCount: number
): number {
  return totalQuestions > 0 
    ? Math.min(99, Math.round((completedQuestionsCount / totalQuestions) * 100))
    : 0;
}

// Build the namespaced localStorage key. When an attemptId is provided
// (premium flow), each attempt gets its own key so multiple ongoing attempts
// of the same assessment don't clobber each other.
function progressKey(assessmentId: string, attemptId?: string): string {
  return attemptId
    ? `assessment-progress-${assessmentId}-attempt-${attemptId}`
    : `assessment-progress-${assessmentId}`;
}

// Save assessment progress to localStorage
export function saveProgressToLocalStorage(
  assessmentId: string,
  categoryIndex: number,
  questionIndex: number,
  step: AssessmentStep,
  personalInfo: PersonalInfo,
  attemptId?: string
): void {
  const key = progressKey(assessmentId, attemptId);
  const savedProgress = localStorage.getItem(key);
  let progressData = {};

  if (savedProgress) {
    try {
      progressData = JSON.parse(savedProgress);
    } catch {
      // ignore
    }
  }

  localStorage.setItem(key, JSON.stringify({
    ...progressData,
    categoryIndex,
    questionIndex,
    step,
    personalInfo
  }));
}

// Load assessment progress from localStorage
export function loadProgressFromLocalStorage(
  assessmentId: string,
  attemptId?: string
): {
  categoryIndex: number;
  questionIndex: number;
  step: AssessmentStep;
  personalInfo: PersonalInfo;
} {
  const defaultValues = {
    categoryIndex: 0,
    questionIndex: 0,
    step: 'welcome' as AssessmentStep,
    personalInfo: {
      fullName: '',
      email: '',
      company: '',
      designation: '',
      consentGiven: false
    }
  };

  const savedProgress = localStorage.getItem(progressKey(assessmentId, attemptId));

  if (!savedProgress) {
    return defaultValues;
  }

  try {
    const parsed = JSON.parse(savedProgress);
    return {
      categoryIndex: parsed.categoryIndex || defaultValues.categoryIndex,
      questionIndex: parsed.questionIndex || defaultValues.questionIndex,
      step: parsed.step || defaultValues.step,
      personalInfo: parsed.personalInfo || defaultValues.personalInfo
    };
  } catch {
    return defaultValues;
  }
}

// Clear assessment progress from localStorage
export function clearProgressFromLocalStorage(
  assessmentId: string,
  attemptId?: string
): void {
  localStorage.removeItem(progressKey(assessmentId, attemptId));
}
